import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../../components/custom.css";
import Text from "../text"; // Assuming text.js is in components folder
import ShareModal from "../shareModal";

// --- Districts array ---
const statesAndDistricts = {
  Andhra_Pradesh: [
    "Anantapur",
    "Chittoor",
    "East Godavari",
    "Guntur",
    "Krishna",
    "Kurnool",
    "Prakasam",
    "Srikakulam",
    "Visakhapatnam",
    "Vizianagaram",
    "West Godavari",
    "YSR Kadapa",
    "Nellore",
    "Alluri Sitharama Raju",
    "Anakapalli",
    "Eluru",
    "Konaseema",
    "Nandyal",
    "Sri Potti Sriramulu Nellore",
    "Parvathipuram Manyam",
    "Sri Sathya Sai",
    "Tirupati",
    "Annamayya",
    "Bapatla",
    "Kakinada",
    "Palnadu",
  ],
  Telangana: [
    "Adilabad",
    "Bhadradri Kothagudem",
    "Hanamkonda",
    "Hyderabad",
    "Jagtial",
    "Jangaon",
    "Jayashankar Bhupalpally",
    "Jogulamba Gadwal",
    "Kamareddy",
    "Karimnagar",
    "Khammam",
    "Kumuram Bheem",
    "Mahabubabad",
    "Mahabubnagar",
    "Mancherial",
    "Medak",
    "Medchal-Malkajgiri",
    "Mulugu",
    "Nagarkurnool",
    "Nalgonda",
    "Narayanpet",
    "Nirmal",
    "Nizamabad",
    "Peddapalli",
    "Rajanna Sircilla",
    "Ranga Reddy",
    "Sangareddy",
    "Siddipet",
    "Suryapet",
    "Vikarabad",
    "Wanaparthy",
    "Warangal",
    "Yadadri Bhuvanagiri",
  ],
};

const LAST_SEARCH_CRITERIA_KEY = "lastUserSearchCriteria";

const InputForm = ({ onSubmit, loading, error, onAdvancedSearch }) => {
  const [formData, setFormData] = useState({
    rank: "",
    gender: "Male",
    caste: "oc",
    examType: "ap",
    year: "2024",
    location: "", // Ensure location is part of the state
  });
  const [numError, setNumError] = useState("");
  const navigate = useNavigate();

  const [isShareTextVisible, setIsShareTextVisible] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const currentPageUrl = window.location.href;

  // Effect to show and hide share text
  useEffect(() => {
    const timer1 = setTimeout(() => {
      setIsShareTextVisible(true);
    }, 300);
    // Hide text after 2 seconds
    const timer = setTimeout(() => {
      setIsShareTextVisible(false);
    }, 2500);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer1);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const newState = { ...prev };
      if (name === "examType") {
        newState.examType = value;
        newState.year =
          value === "ap" || value === "ap_bipc"
            ? "2024" // Changed from "2023" to "2024"
            : value === "ts_bipc"
            ? "first"
            : "2024_first";
        newState.location = ""; // Reset location when examType changes
      } else {
        newState[name] = value;
      }
      return newState;
    });

    if (name === "rank") {
      const numValue = Number(value);
      if (value === "") setNumError("Rank is required");
      else if (isNaN(numValue)) setNumError("Please enter a valid number");
      else if (numValue < 1) setNumError("Rank must be at least 1");
      else if (numValue > 500000) setNumError("Rank cannot exceed 500,000");
      else setNumError("");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (numError) {
      alert("Please correct the rank error.");
      return;
    }
    if (
      !formData.location &&
      (formData.examType === "ap" ||
        formData.examType === "ap_bipc" ||
        formData.examType === "ts" ||
        formData.examType === "ts_bipc")
    ) {
      alert("Please select a location (district).");
      return;
    }
    onSubmit(formData);
  };

  const handleAdvancedClick = () => {
    if (numError) {
      alert("Please correct the rank error before creating a list.");
      return;
    }
    const isLocationRequired =
      formData.examType === "ap" ||
      formData.examType === "ap_bipc" ||
      formData.examType === "ts" ||
      formData.examType === "ts_bipc";
    if (
      !formData.rank ||
      !formData.gender ||
      !formData.caste ||
      !formData.examType ||
      !formData.year ||
      (isLocationRequired && !formData.location)
    ) {
      alert("Please fill all required fields before creating a list.");
      return;
    }

    const currentCriteriaString = JSON.stringify(formData);
    navigate("/filter", { state: { inputData: formData } });
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 max-w-4xl mx-auto">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 lg:mb-6 flex">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 lg:h-7 lg:w-7 mr-2 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            Search Colleges
          </h2>

          {/* New 2024 Cutoff Animation - Responsive */}
          <div className="ml-0 sm:ml-4 mb-4 lg:mb-6">
            <div className="relative overflow-hidden">
              <div className="animate-slideInFromLeft bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-bold shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center justify-center">
                  <div className="relative">
                    {/* Shining pill for NEW */}
                    <div className="relative bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 text-black px-2 py-0.5 rounded-full text-xs font-black shadow-md mr-2 overflow-hidden">
                      <span className="relative z-10">NEW</span>
                      {/* Shine effect */}
                      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30 transform -skew-x-12 animate-shine"></div>
                    </div>
                  </div>
                  <span className="animate-bounce inline-block mr-1">✨</span>
                  <span className="whitespace-nowrap">
                    {" "}
                    2025 COLLEGES RELEASED
                  </span>
                </div>
              </div>
              {/* Subtle sparkle effects */}
              <div className="absolute -top-0.5 -right-0.5 animate-ping">
                <div className="w-2 h-2 bg-yellow-400 rounded-full opacity-60"></div>
              </div>
              <div className="absolute -bottom-0.5 -left-0.5 animate-ping animation-delay-500">
                <div className="w-1.5 h-1.5 bg-blue-300 rounded-full opacity-60"></div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="relative flex flex-col items-end mt-2 lg:mt-0"
          onMouseEnter={() => setIsShareTextVisible(true)}
          onMouseLeave={() => setIsShareTextVisible(false)}
        >
          {/* Share Tooltip */}
          <div
            className={`
              absolute right-12 -top-1 lg:right-28 bg-blue-300 text-gray-800 px-4 py-2 rounded-lg shadow-lg 
              text-sm font-medium transition-all duration-300 ease-in-out whitespace-nowrap
              ${
                isShareTextVisible
                  ? "opacity-100 translate-x-0 scale-100"
                  : "opacity-0 translate-x-8 scale-95"
              }
            `}
          >
            Share With Friends
            {/* Arrow/Spoke pointing to the share button */}
            <div className="absolute top-1/2 -translate-y-1/2 -right-2 w-0 h-0 border-l-8 border-l-blue-300 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
          </div>
          <button
            onClick={() => setIsShareModalOpen(true)}
            className="ml-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-3 sm:px-4 rounded-lg flex items-center shadow-sm transition-colors text-sm"
            aria-label="Share this page"
          >
            <svg
              className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6.001l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
              ></path>
            </svg>
            <span className="hidden sm:inline">Share</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
          <p className="font-medium">Error</p>
          <p>{error}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <div className="space-y-6">
          {/* Exam Type Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Exam Type<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="examType"
                value={formData.examType}
                onChange={handleChange}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none bg-white"
              >
                <option value="ap">AP EAPCET (Engineering)</option>
                <option value="ap_bipc">AP EAPCET (B.Pharmacy)</option>
                <option value="ts">TS EAPCET (Engineering)</option>
                <option value="ts_bipc">TS EAPCET (B.Pharmacy)</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Year Selection */}
          {(formData.examType === "ap" || formData.examType === "ap_bipc") && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                Select Year Wise Cutoff<span className="text-red-500">*</span>
              </label>
               <select
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none bg-white"
              >
                <option value="2024">2024 (NEW)</option>
                <option value="2023">2023</option>
                <option value="2022">2022</option>
                <option value="2021">2021</option>
                <option value="2020">2020</option>
              </select>
            </div>
          )}
          {formData.examType === "ts" && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                Year<span className="text-red-500">*</span>
              </label>
              <select
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none bg-white"
              >
                <option value="2024_first">2024 First Phase</option>
                <option value="2024_second">2024 Second Phase</option>
                <option value="2024_final">2024 Final Phase</option>
                <option value="2023_first">2023 First Phase</option>
                <option value="2023_final">2023 Final Phase</option>
                <option value="2022_first">2022 First Phase</option>
                <option value="2022_final">2022 Final Phase</option>
                <option value="2021_first">2021 First Phase</option>
                <option value="2021_final">2021 Final Phase</option>
              </select>
            </div>
          )}
          {formData.examType === "ts_bipc" && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                Year<span className="text-red-500">*</span>
              </label>
              <select
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none bg-white"
              >
                <option value="first">2024 First Phase</option>
                <option value="final">2024 Final Phase</option>
              </select>
            </div>
          )}

          {/* Rank Input */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Your Rank<span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="rank"
              value={formData.rank}
              onChange={handleChange}
              max="500000"
              min="1"
              maxLength={6}
              required
              placeholder="Enter your Expected rank"
              className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm ${
                numError ? "border-red-500" : "border-gray-300"
              }`}
            />
            {numError && (
              <p className="text-red-500 text-sm mt-1">{numError}</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Gender Selection */}
          <div>
            <label className="text-sm better-font-medium text-gray-700 mb-1 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Gender<span className="text-red-500">*</span>
            </label>
            <div className="flex items-center space-x-4 mt-2 bg-gray-50 p-2.5 rounded-lg border border-gray-200 group-hover:border-blue-300 transition-all">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="male"
                  name="gender"
                  value="Male"
                  checked={formData.gender === "Male"}
                  onChange={handleChange}
                  required
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label
                  htmlFor="male"
                  className="ml-2 text-gray-800 text-sm font-medium"
                >
                  Male
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="female"
                  name="gender"
                  value="Female"
                  checked={formData.gender === "Female"}
                  onChange={handleChange}
                  required
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label
                  htmlFor="female"
                  className="ml-2 text-gray-800 text-sm font-medium"
                >
                  Female
                </label>
              </div>
            </div>
          </div>

          {/* Caste Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              Category<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="caste"
                value={formData.caste}
                onChange={handleChange}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none bg-white"
              >
                <option value="">Select category</option>
                <option value="oc">General</option>
                <option value="ews">EWS</option>
                <option value="bca">BC-A</option>
                <option value="bcb">BC-B</option>
                <option value="bcc">BC-C</option>
                <option value="bcd">BC-D</option>
                <option value="bce">BC-E</option>
                <option value="sc">SC</option>
                <option value="st">ST</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
          {/* Location (District) Field */}
          {(formData.examType === "ap" ||
            formData.examType === "ap_bipc" ||
            formData.examType === "ts" ||
            formData.examType === "ts_bipc") && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Location (District)<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none bg-white"
                >
                  <option value="">Select District</option>
                  {(formData.examType === "ap" ||
                    formData.examType === "ap_bipc") &&
                    statesAndDistricts.Andhra_Pradesh.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  {(formData.examType === "ts" ||
                    formData.examType === "ts_bipc") &&
                    statesAndDistricts.Telangana.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg
                    className="fill-current h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="col-span-1 md:col-span-2 flex flex-col sm:flex-row gap-3 mt-4">
          <button
            type="submit"
            disabled={loading || !!numError}
            className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {Text.Searching}
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Find Colleges
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleAdvancedClick}
            className="flex-1 px-4 py-3 bg-blue-200 hover:bg-blue-700 text-blue-800 hover:text-white font-medium rounded-lg transition-colors flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
            Create List
          </button>
        </div>
      </form>

      <ShareModal
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        link={currentPageUrl}
      />
    </div>
  );
};

export default InputForm;
